import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaBuilding, FaList, FaTh, FaPlus } from "react-icons/fa";
import {
  getCompanies,
  setFilters,
  setPage,
  setLimit,
  setViewMode,
} from "../../features/companies/companiesSlice";
import { AppDispatch, RootState } from "../../store";
import CompaniesFilter from "./components/CompaniesFilter";
import CompaniesList from "./components/CompaniesList";
import CompaniesGrid from "./components/CompaniesGrid";
import { Pagination } from "react-bootstrap";
import ErrorAlert from "../../components/common/ErrorAlert";
import { Icon } from "../../components/common/Icon";
import { useTranslation } from "react-i18next";
import BrandGlyph from "../../components/common/BrandGlyph";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useTheme } from "../../context/ThemeContext";
import CompanyFormModal from "./components/CompanyFormModal";

interface CompaniesPageProps {
  isNew?: boolean;
  isDetail?: boolean;
  isEdit?: boolean;
}

const CompaniesPage: React.FC<CompaniesPageProps> = ({
  isNew = false,
  isDetail = false,
  isEdit = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const {
    companies,
    isLoading,
    error,
    totalCount,
    page,
    limit,
    filters,
    viewMode,
  } = useSelector((state: RootState) => state.companies);

  const [showCreateModal, setShowCreateModal] = useState(isNew);
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialCompanyType = query.get("type") === "agency" ? "agency" : query.get("type") === "client" ? "client" : query.get("type") === "single" ? "single" : undefined;

  // Eğer route isNew ise modali aç; değilse query param gelirse de aç
  useEffect(() => {
    if (initialCompanyType && !showCreateModal) {
      setShowCreateModal(true);
    }
  }, [initialCompanyType]);
  const [companyTypeFilter, setCompanyTypeFilter] = useState<string>("all");

  // Şirketleri yükle
  useEffect(() => {
    const updatedFilters = { ...filters };
    if (companyTypeFilter !== "all") {
      updatedFilters.company_type = companyTypeFilter;
    } else {
      delete updatedFilters.company_type;
    }

    dispatch(setFilters(updatedFilters));
  }, [companyTypeFilter]);

  // Filtreler, sayfa veya limit değiştiğinde şirketleri yeniden yükle
  useEffect(() => {
    dispatch(getCompanies({ ...filters, page, limit }));
  }, [dispatch, filters, page, limit]);

  // Görünüm modunu değiştir
  const handleViewModeChange = (mode: "list" | "grid") => {
    dispatch(setViewMode(mode));
  };

  // Handle sorting
  const handleSort = (field: string) => {
    const isAsc = filters.sort_by === field && filters.sort_order === "asc";
    const newFilters = {
      ...filters,
      sort_by: field,
      sort_order: (isAsc ? "desc" : "asc") as "asc" | "desc",
    };
    dispatch(setFilters(newFilters));
  };

  const getSortIcon = (field: string) => {
    if (filters.sort_by !== field) {
      return <i className="bi bi-arrow-down-up ms-2 text-muted" style={{ fontSize: '0.8rem' }}></i>;
    }
    return filters.sort_order === "asc" ? (
      <i className="bi bi-sort-up ms-2 text-primary"></i>
    ) : (
      <i className="bi bi-sort-down ms-2 text-primary"></i>
    );
  };

  // Sayfa değişikliği
  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  // Sayfa başına öğe sayısı değişikliği
  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setLimit(Number(e.target.value)));
    dispatch(setPage(1));
  };

  // Şirket türü filtreleme
  const handleCompanyTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanyTypeFilter(e.target.value);
    dispatch(setPage(1));
  };

  // Calculate pagination
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / limit) : 1;
  const paginationItems = [];

  for (let i = 1; i <= totalPages; i++) {
    // Only show current page, 2 pages before and 2 pages after
    if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
      paginationItems.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    } else if (i === page - 3 || i === page + 3) {
      paginationItems.push(<Pagination.Ellipsis key={i} disabled />);
    }
  }

  const activeCount = companies.filter((c) => c.is_active).length;

  return (
    <div className="companies-page animate-fade-in">
      {/* Modern Header */}
      <div className="modern-header slide-in-down mb-4">
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <div className="header-icon">
                <Icon icon={FaBuilding} size="1.5rem" className="text-white" />
              </div>
            </div>
            <div className="header-text">
              <h1 className="header-title">{t("company_management")}</h1>
              <p className="header-subtitle">
                {totalCount} {t("total_companies")} • {activeCount} {t("active")}
              </p>
            </div>
          </div>
          <div className="header-actions">
            <ButtonGroup size="sm" className="companies-view-toggle me-2">
              <Button
                variant={viewMode === "list" ? "primary" : "outline-secondary"}
                onClick={() => handleViewModeChange("list")}
                className="view-toggle-btn"
                aria-label={t("list_view") || "List view"}
              >
                <Icon icon={FaList} size="1rem" />
              </Button>
              <Button
                variant={viewMode === "grid" ? "primary" : "outline-secondary"}
                onClick={() => handleViewModeChange("grid")}
                className="view-toggle-btn"
                aria-label={t("grid_view") || "Grid view"}
              >
                <Icon icon={FaTh} size="1rem" />
              </Button>
            </ButtonGroup>
            <Button
              variant="primary"
              className="btn-modern btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Icon icon={FaPlus} className="me-2" />
              <span>{t("new_company")}</span>
            </Button>
          </div>
        </div>
      </div>

      {error && <ErrorAlert message={error} />}

      {/* Filters Section */}
      <CompaniesFilter />

      {/* Main Content area */}
      <div className="content-area-box">
        {isLoading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <span className="text-muted">{t("loading_companies")}</span>
          </div>
        ) : (
          <>
            {/* List View */}
            {viewMode === "list" && (
              <div className="table-container-premium shadow-sm">
                {companies.length > 0 ? (
                  <CompaniesList 
                    companies={companies} 
                    onSort={handleSort}
                    getSortIcon={getSortIcon}
                    filters={filters}
                  />
                ) : (
                  <div className="empty-state-box py-5">
                    <Icon icon={BrandGlyph} size={64} className="text-muted opacity-25 mb-3" />
                    <h5 className="text-muted">{t("no_companies_found")}</h5>
                    <p className="text-muted small mb-4">{t("try_adjusting_filters_or_add_new_company")}</p>
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowCreateModal(true)}
                      className="rounded-pill px-4"
                    >
                      {t("add_first_company")}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Grid View */}
            {viewMode === "grid" && (
              <div className="grid-container-premium">
                {companies.length > 0 ? (
                  <CompaniesGrid companies={companies} />
                ) : (
                  <div className="empty-state-box py-5 w-100 text-center">
                    <Icon icon={BrandGlyph} size={64} className="text-muted opacity-25 mb-3" />
                    <h5 className="text-muted">{t("no_companies_found")}</h5>
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowCreateModal(true)}
                      className="rounded-pill px-4 mt-3"
                    >
                      {t("add_first_company")}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination Section */}
      {!isLoading && totalCount > 0 && (
        <div className="pagination-footer-premium mt-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="rows-select-wrapper d-flex align-items-center gap-2">
              <span className="text-muted small">{t("rows_per_page")}:</span>
              <Form.Select
                size="sm"
                value={limit}
                onChange={handleLimitChange}
                className="form-select-premium"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </Form.Select>
            </div>
            <div className="pagination-text-info text-muted small">
              {t("showing")} <span className="fw-bold text-primary">{(page - 1) * limit + 1}</span>-
              <span className="fw-bold text-primary">{Math.min(page * limit, totalCount)}</span> / 
              <span className="fw-bold text-primary"> {totalCount}</span>
            </div>
          </div>

          <Pagination className="pagination-premium mb-0">
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={page === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            />
            {paginationItems}
            <Pagination.Next
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={page === totalPages}
            />
          </Pagination>
        </div>
      )}

      <CompanyFormModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        initialCompanyType={initialCompanyType}
      />

      <style>{`
        .companies-page {
          padding: 1.5rem;
          max-width: 1600px;
          margin: 0 auto;
        }

        .table-container-premium {
          background: var(--bg-elevated, white);
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid var(--border-subtle);
        }

        .empty-state-box {
          text-align: center;
          background: var(--bg-elevated, white);
          border-radius: 20px;
        }

        @media (max-width: 992px) {
          .companies-page {
            padding: 1rem;
          }
        }

        @media (max-width: 768px) {
          .companies-page {
            padding: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CompaniesPage;
